import type { BuildingLayerItem, BuildingSubLayer } from "features/dt-basic-setup/single-building/model/types";

import { SingleBuildingContext } from "features/dt-basic-setup/single-building/context/AddSingleBuildingContext";
import { useCallback, useContext, useMemo, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";

import { SUBLAYER_PRESET_KEYS } from "../constants";
import { migrateLayers } from "../lib/migrateLayers";
import { initialLayerStepState, layerStepReducer } from "../reducer";
import { useLayersStepActions } from "./useLayersStepActions";
import {
  useClampActiveLayerEffect,
  useClampActiveSubLayerEffect,
  useEndDrawingOnActiveLayerOrSubLayerChangeEffect,
  useEndDrawingWhenCannotDrawEffect,
  useFinishDrawingOnEnterKeyEffect,
  useSyncNewSubLayerKeyToLayerPresetEffect,
} from "./useLayersStepEffects";

export function useLayersStep() {
  const { t } = useTranslation();
  const { formData, setFormData } = useContext(SingleBuildingContext);

  const layers = useMemo(() => migrateLayers(formData?.layers), [formData?.layers]);
  const [layerUi, dispatch] = useReducer(layerStepReducer, undefined, initialLayerStepState);
  const drawingObjectIdRef = useRef<null | string>(null);

  const commitLayers = useCallback(
    (updater: (prev: BuildingLayerItem[]) => BuildingLayerItem[]) => {
      setFormData((fd) => ({ ...fd, layers: updater(migrateLayers(fd.layers)) }));
    },
    [setFormData]
  );

  const endDrawingSession = useCallback(() => {
    drawingObjectIdRef.current = null;
    dispatch({ type: "END_DRAWING_SESSION" });
  }, []);

  const activeLayer = layers.find((layer) => layer.id === layerUi.activeLayerId);
  const activeSubLayer = activeLayer?.subLayers.find((s) => s.id === layerUi.activeSubLayerId);

  const finishCurrentPolygon = useCallback(() => {
    if (!drawingObjectIdRef.current) return;
    drawingObjectIdRef.current = null;
    dispatch({ id: null, type: "SET_SELECTED_OBJECT" });
  }, []);
  const activeObjects = activeSubLayer?.objects ?? [];
  const mapObjects = activeLayer && activeLayer.visible !== false ? activeObjects : [];
  const canDraw = Boolean(activeLayer && activeSubLayer);

  useClampActiveLayerEffect(layers, layerUi.activeLayerId, dispatch);
  useClampActiveSubLayerEffect(activeLayer, layerUi.activeSubLayerId, dispatch);
  useEndDrawingWhenCannotDrawEffect(canDraw, endDrawingSession);
  useEndDrawingOnActiveLayerOrSubLayerChangeEffect(layerUi.activeLayerId, layerUi.activeSubLayerId, endDrawingSession);
  useSyncNewSubLayerKeyToLayerPresetEffect(activeLayer, layerUi.activeLayerId, dispatch);
  useFinishDrawingOnEnterKeyEffect(canDraw, layerUi.isDrawing, finishCurrentPolygon);

  const layerDisplayName = useCallback(
    (layer: BuildingLayerItem) => {
      const trimmed = layer.name.trim();
      if (trimmed) return trimmed;
      return t(`AddBuilding.layersStep.layerPresetLabels.${layer.preset}`);
    },
    [t]
  );

  const subLayerDisplayName = useCallback(
    (layer: BuildingLayerItem, sub: BuildingSubLayer, index: number) => {
      const trimmed = sub.name.trim();
      if (sub.nameFromPreset) {
        if (trimmed && SUBLAYER_PRESET_KEYS[layer.preset]?.includes(trimmed)) {
          return t(`AddBuilding.layersStep.sublayerPresetLabels.${layer.preset}.${trimmed}`);
        }
        if (trimmed) return trimmed;
        return t("AddBuilding.layersStep.sublayerDefault", { n: index + 1 });
      }
      if (trimmed) return trimmed;
      return t("AddBuilding.layersStep.sublayerDefault", { n: index + 1 });
    },
    [t]
  );

  const updateLayer = useCallback(
    (layerId: string, updater: (layer: BuildingLayerItem) => BuildingLayerItem) => {
      commitLayers((prev) => prev.map((layer) => (layer.id === layerId ? updater(layer) : layer)));
    },
    [commitLayers]
  );

  const { formActions, mapActions } = useLayersStepActions({
    activeLayer,
    activeSubLayer,
    canDraw,
    commitLayers,
    dispatch,
    drawingObjectIdRef,
    endDrawingSession,
    layers,
    layerUi,
    subLayerDisplayName,
    updateLayer,
  });

  return {
    formActions,
    layerLabels: {
      layerDisplayName,
      subLayerDisplayName,
    },
    layerScope: {
      activeLayer,
      activeSubLayer,
      buildingCenter: formData.buildingCenter,
      canDraw,
      drawingObjectId: drawingObjectIdRef.current,
      layers,
      mapObjects,
    },
    layerUi,
    mapActions: {
      ...mapActions,
      finishCurrentPolygon,
    },
  };
}
